import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { saveAs } from "file-saver";
import { Cookies } from "react-cookie";
import moment from "moment";
import CryptoJS from "crypto-js";
import { Switch, Select, Tag, Spin, Button } from "antd";
import SkeletonTable from "../../components/table/SkeletonTable.jsx";
import columns from "../../columns/clients/clients.js";
import ConfirmationModal from "../../components/modals/ConfirmationModal.jsx";
import { LoadingOutlined } from "@ant-design/icons";
import Add from "../../assets/Add.jsx";
import Eye from "../../assets/Eye.jsx";
import Delete from "../../assets/Delete.jsx";
import ArrowIcon from "../../assets/Login.jsx";
import AddClientForm from "./components/AddClientForm.jsx";
import LoadableButton from "../../components/buttons/LoadableButton.jsx";
import { GetTagListApi } from "../../api/request/settings/tags.js";
import {
  AddClientTagApi,
  DeleteClientApi,
  GetClientsApi,
  ChangeClientStatusApi,
  ExportClientsApi,
} from "../../api/request/clients/clients";

export default function Clients() {
  const navigate = useNavigate();
  const userDetails = useSelector((state) => state.user.userDetails);
  var SecretKey = process.env.REACT_APP_SECRET_KEY;
  const primaryLightColor = getComputedStyle(document.documentElement)
    .getPropertyValue("--primaryLight")
    ?.trim();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [isStatusLoading, setIsStatusLoading] = useState(false);
  const [tags, setTags] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExportLoading, setIsExportLoading] = useState(false);
  const [isClientDelete, setIsClientDelete] = useState(false);
  const [clientId, setClientId] = useState(null);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [pagination, setPagination] = useState(() => {
    try {
      const saved = sessionStorage.getItem("clients_pagination");
      return saved ? JSON.parse(saved) : { current: 1, pageSize: 10 };
    } catch {
      return { current: 1, pageSize: 10 };
    }
  });

  const handleTableChange = (newPagination) => {
    const updated = {
      current: newPagination?.current || 1,
      pageSize: newPagination?.pageSize || 10,
    };
    setPagination(updated);
    try {
      sessionStorage.setItem("clients_pagination", JSON.stringify(updated));
    } catch {}
  };

  const tagRender = (props) => {
    const { label, onClose } = props;
    const onPreventMouseDown = (event) => {
      event.preventDefault();
      event.stopPropagation();
    };
    return (
      <Tag
        color={primaryLightColor}
        onMouseDown={onPreventMouseDown}
        style={{ marginInlineEnd: 4 }}
        className="flex items-center gap-2"
      >
        <p className="text-primary">{label}</p>{" "}
        <p className="cursor-pointer text-secondaryLight1" onClick={onClose}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="size-4"
          >
            <path
              fill-rule="evenodd"
              d="M5.47 5.47a.75.75 0 0 1 1.06 0L12 10.94l5.47-5.47a.75.75 0 1 1 1.06 1.06L13.06 12l5.47 5.47a.75.75 0 1 1-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 0 1-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 0 1 0-1.06Z"
              clip-rule="evenodd"
            />
          </svg>
        </p>
      </Tag>
    );
  };

  const getClient = async () => {
    try {
      setIsLoading(true);
      const { data } = await GetClientsApi();
      if (data?.status) {
        setData(
          data?.data?.map((item) => {
            return {
              ...item,
              organizerText: item?.organizer,
              organizer: (
                <p
                  className={`font-bold capitalize ${item?.organizer === "finished" ? "text-green-500" : ""
                    }`}
                >
                  {item?.current_task || item?.organizer}
                </p>
              ),
              paymentText: item?.payment,
              payment: item?.payment ? (
                <p
                  className={`font-bold capitalize ${item?.payment === "Paid" ? "text-green-500" : "text-red-600"
                    }`}
                >
                  {item?.payment}
                </p>
              ) : null,
              signatureText: item?.signature,
              signature: item?.signature ? (
                <p
                  className={`font-bold capitalize ${item?.signature === "completed" ? "text-green-500" : ""
                    }`}
                >
                  {item?.signature}
                </p>
              ) : null,
              taskText: item?.task,
              tag: (
                <Select
                  mode="tags"
                  tagRender={tagRender}
                  placeholder="Select tag"
                  style={{ width: "100%" }}
                  defaultValue={item?.client_tag ? [item?.client_tag] : []}
                  options={tags}
                  onChange={(value) =>
                    tagHandler(item?.id, value, item?.client_tag)
                  }
                />
              ),
              statusTag: (
                <Switch
                  checked={item?.status === "active" ? true : false}
                  onClick={() => handleClientStatus(item)}
                  checkedChildren="On"
                  unCheckedChildren="Off"
                />
              ),
              created_at: item?.created_at
                ? moment(item?.created_at).format("LL")
                : "",
              action: (
                <div className="flex items-center gap-5">
                  <div
                    className="cursor-pointer"
                    onClick={() => navigate(`/client/${item?.id}`)}
                  >
                    <Eye />
                  </div>
                  {userDetails?.role === "admin" && (
                    <div
                      className="cursor-pointer"
                      onClick={() => handleLogin(item, "redirect")}
                    >
                      <ArrowIcon fill="black" />
                    </div>
                  )}
                  <button
                    onClick={() => {
                      setClientId(item?.id);
                      setIsClientDelete(true);
                    }}
                  >
                    <Delete />
                  </button>
                </div>
              ),
            };
          })
        );
        setTotal(data?.total_record);
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  const getClientTags = async () => {
    try {
      setIsLoading(true);
      const { data } = await GetTagListApi({ type: "client" });
      if (data.status) {
        setTags(
          data?.data?.map((item) => {
            return { value: item?.name };
          })
        );
      }
      setIsLoading(false);
    } catch (error) { }
  };

  useEffect(() => {
    getClientTags();
  }, []);

  useEffect(() => {
    getClient();
    if (tags?.length > 0) {
      columns.forEach((item) => {
        if (item.dataIndex === "tag") {
          item.filters = tags.map((filter) => {
            return { text: filter?.value, value: filter?.value };
          });
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags]);

  const handleClientStatus = (item) => {
    setSelectedClient(item);
    setIsStatusModalOpen(true);
  };

  const updateClientStatus = (id, status) => {
    setData((prev) => {
      return prev.map((item) => {
        if (item?.id === id) {
          return {
            ...item,
            statusTag: (
              <Switch
                checked={status === "active" ? true : false}
                onClick={() => handleClientStatus({ ...item, status })}
                checkedChildren="On"
                unCheckedChildren="Off"
              />
            ),
          };
        }
        return item;
      });
    });
  };

  const handleStatuChange = async () => {
    setIsStatusLoading(true);
    try {
      const status =
        selectedClient?.status === "active" ? "inactive" : "active";
      const params = { id: selectedClient?.id, status };
      const { data } = await ChangeClientStatusApi(params);
      if (data?.status) {
        toast.success(data?.message);
        setIsStatusModalOpen(false);
        updateClientStatus(selectedClient?.id, status);
      }
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      setIsStatusLoading(false);
    }
  };

  const handleLogin = async (user, type) => {
    const cookies = new Cookies();
    const { token } = cookies.getAll();
    var message = JSON.stringify({ token, user });
    var encrypted = CryptoJS.AES.encrypt(message, SecretKey);
    var safeCiphertext = encrypted.toString();

    const redirectUrl = `${process.env.REACT_APP_CLIENT_URL
      }/admin_login?token=${encodeURIComponent(safeCiphertext)}`;
    if (type === "redirect") {
      window.open(redirectUrl);
    } else {
      navigator.clipboard.writeText(redirectUrl);
      toast.success("Copied to clipboard");
    }
  };

  const spinner = (
    <>
      <div className="flex justify-center items-center my-1">
        <Spin
          indicator={
            <LoadingOutlined
              style={{
                fontSize: 24,
                color: "$theme",
              }}
              spin
            />
          }
        />
      </div>
    </>
  );

  const tagHandler = async (id, tagArray, currentTag) => {
    const findNonMatchingString = () => {
      const nonMatching = tagArray.filter((item) => item !== currentTag);
      return nonMatching.length > 0 ? nonMatching[0] : null;
    };

    try {
      let tag = findNonMatchingString();
      setData((prev) => {
        return prev.map((item) => {
          if (item.id === id) {
            return { ...item, tag: spinner };
          } else {
            return { ...item };
          }
        });
      });
      let params = { id: id, tag: tag };
      const { data } = await AddClientTagApi(params);
      if (data?.status) {
        setData((prev) => {
          return prev.map((item) =>
            item.id === id
              ? {
                ...item,
                client_tag: tag,
                tag: (
                  <Select
                    mode="tags"
                    tagRender={tagRender}
                    placeholder="Select tag"
                    style={{ width: "100%" }}
                    defaultValue={[tag]}
                    options={tags}
                    onChange={(value) => tagHandler(item?.id, value, tag)}
                  />
                ),
                statusTag: (
                  <Switch
                    checked={item?.status === "active" ? true : false}
                    onClick={() => handleClientStatus(item)}
                    checkedChildren="On"
                    unCheckedChildren="Off"
                  />
                ),
                created_at: item?.created_at
                  ? moment(item?.created_at).format("LL")
                  : "",
                action: (
                  <div
                    className="cursor-pointer"
                    onClick={() => navigate(`/client/${item?.id}`)}
                  >
                    <Eye />
                  </div>
                ),
              }
              : item
          );
        });
      }
    } catch (error) { }
  };

  const exportClients = async () => {
    try {
      setIsExportLoading(true);
      const headers = { responseType: "arraybuffer" };
      const { data } = await ExportClientsApi({}, headers);
      const blob = new Blob([data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const timestamp = new Date().toISOString();
      saveAs(blob, `clients_${timestamp}.xlsx`);
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      setIsExportLoading(false);
    }
  };

  const handleDeleteClient = async () => {
    try {
      setIsDeleteLoading(true);
      const { data } = await DeleteClientApi({ client_id: clientId });
      if (data?.status) {
        toast.success(data?.message);
        getClient();
        setIsClientDelete(false);
      }
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      setIsDeleteLoading(false);
    }
  };
  
  return (
    <div>
      <div className="border-2 rounded-xl mb-4">
        <div className="flex justify-between mt-4 mx-5">
          <p className="font-semibold text-xl">
            Clients {total ? `(${total})` : null}
          </p>
          <div className="flex gap-3">
            <Button
              type="primary"
              className="font-medium uppercase"
              icon={<Add />}
              onClick={() => setIsModalOpen(true)}
            >
              Add
            </Button>
            {userDetails?.role === "admin" && (
              <LoadableButton
                className="bg-primary text-white text-sm font-medium uppercase px-4 py-1.5 rounded-md"
                lable={"Export"}
                onClick={exportClients}
                isLoading={isExportLoading}
                loadingLable="Exporting..."
              />
            )}
          </div>
        </div>
        <div className="table-radius-0 text-light my-4">
          <SkeletonTable
            searchingFields={["name", "email", "mobile_no"]}
            dateFilterFields = {["created_at"]}
            columns={columns}
            data={data}
            isLoading={isLoading}
            pagination={pagination}
            handleTableChange={handleTableChange}
          />
        </div>
      </div>

      {/* Add New Client Modal  */}
      <AddClientForm
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        getClient={getClient}
      />

      {/* ------ Confirmation modal for status ------- */}
      <ConfirmationModal
        isOpen={isStatusModalOpen}
        setIsOpen={setIsStatusModalOpen}
        message="Are you sure, you want to change client status?"
        onConfirm={handleStatuChange}
        isLoading={isStatusLoading}
        loadingLabel="Changing status..."
      />

      {/* Delete Client */}
      <ConfirmationModal
        isOpen={isClientDelete}
        setIsOpen={setIsClientDelete}
        message="Are you sure, want to delete this client?"
        onConfirm={() => handleDeleteClient()}
        isLoading={isDeleteLoading}
        loadingLabel="Deleting Client..."
      />
    </div>
  );
}
